import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import "./DestinationDetail.css";
import Navbar from "./Navbar";
import Footer from "./Footer";

const DestinationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dest, setDest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [linkedTours, setLinkedTours] = useState([]);
  const [toursLoading, setToursLoading] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [expandedTour, setExpandedTour] = useState(null);

  // Fetch destination
  useEffect(() => {
    const fetchDest = async () => {
      try {
        const snap = await getDoc(doc(db, "destinations", id));
        if (snap.exists()) {
          setDest({ id: snap.id, ...snap.data() });
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error("Error fetching destination:", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchDest();
  }, [id]);

  // Fetch linked tours + packages
  useEffect(() => {
    if (!dest?.linkedTourIds?.length) return;
    const fetchTours = async () => {
      setToursLoading(true);
      try {
        const tourMap = {};
        await Promise.all(
          dest.linkedTourIds.map(async (tourId) => {
            try {
              const tourSnap = await getDoc(doc(db, "tours", tourId));
              if (tourSnap.exists()) {
                const tourData = { id: tourSnap.id, ...tourSnap.data() };
                if (tourData.isActive === true) {
                  const pkgsSnap = await getDocs(
                    query(
                      collection(db, "tours", tourId, "packages"),
                      where("isActive", "==", true),
                      orderBy("createdAt", "desc")
                    )
                  );
                  tourData.packages = pkgsSnap.docs.map((p) => ({
                    id: p.id,
                    ...p.data(),
                  }));
                  tourMap[tourId] = tourData;
                }
              }
            } catch (e) {
              console.warn(`Failed to load tour ${tourId}:`, e);
            }
          })
        );
        const activeTours = dest.linkedTourIds
          .map((tid) => tourMap[tid])
          .filter(Boolean);
        setLinkedTours(activeTours);
        if (activeTours.length > 0) {
          setExpandedTour(activeTours[0].id);
        }
      } catch (e) {
        console.error("Error fetching tours:", e);
      } finally {
        setToursLoading(false);
      }
    };
    fetchTours();
  }, [dest]);

  // Reading progress bar
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setReadingProgress(Math.min(progress, 100));
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll to top on load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const handleEnquire = (pkg, tourName) => {
    window.open(
      `https://wa.me/919096785868?text=Hi! I'm interested in the "${pkg.packageName}" package from the "${tourName}" tour at ${dest.name}.`,
      "_blank"
    );
  };

  const handleGeneralEnquire = () => {
    window.open(
      `https://wa.me/919096785868?text=Hi! I'm interested in visiting ${dest.name}.`,
      "_blank"
    );
  };

  // ── LOADING ──
  if (loading) {
    return (
      <div className="dd-loading-screen">
        <div className="dd-loading-spinner"></div>
        <p>Loading destination details...</p>
      </div>
    );
  }

  // ── NOT FOUND ──
  if (notFound || !dest) {
    return (
      <div className="dd-notfound">
        <i className="fas fa-map-signs"></i>
        <h2>Destination not found</h2>
        <p>This destination may have been removed or the link is invalid.</p>
        <button onClick={() => navigate("/destinations")}>
          <i className="fas fa-arrow-left"></i> Back to Destinations
        </button>
      </div>
    );
  }

  const location = [dest.state, dest.country].filter(Boolean).join(", ");
  const images = dest.imageUrls || [];

  return (
    <div className="dd-page">
      {/* Reading Progress Bar */}
      <div className="dd-progress-bar" style={{ width: `${readingProgress}%` }} />

      {/* ── NAVBAR ── */}
      <Navbar />

      {/* ── HERO / COVER ── */}
      <header className="dd-hero">
        {images.length > 0 ? (
          <img className="dd-hero-img" src={images[0]} alt={dest.name} />
        ) : (
          <div className="dd-hero-placeholder">
            <i className="fas fa-globe-asia"></i>
          </div>
        )}
        <div className="dd-hero-overlay">
          {/* Back button */}
          <button className="dd-back-btn" onClick={() => navigate("/destinations")}>
            <i className="fas fa-arrow-left"></i> All Destinations
          </button>

          {/* Tags */}
          <div className="dd-hero-tags">
            {location && <span className="dd-hero-tag">📍 {location}</span>}
            {dest.bestTimeToVisit && (
              <span className="dd-hero-tag">🗓 {dest.bestTimeToVisit}</span>
            )}
            {dest.language && <span className="dd-hero-tag">🗣 {dest.language}</span>}
            {dest.currency && <span className="dd-hero-tag">💱 {dest.currency}</span>}
          </div>

          <h1 className="dd-hero-title">{dest.name}</h1>

          {/* Meta row */}
          <div className="dd-hero-meta">
            {dest.tagline && <span>✨ "{dest.tagline}"</span>}
          </div>
        </div>
      </header>

      {/* ── MAIN CONTENT ── */}
      <main className="dd-main">
        <div className="dd-content-wrap">
          {/* Tagline / Quote */}
          {dest.tagline && (
            <blockquote className="dd-quote">
              <i className="fas fa-quote-left dd-quote-icon"></i>
              <p>{dest.tagline}</p>
            </blockquote>
          )}

          {/* Description Body */}
          {dest.description && (
            <div className="dd-body">
              {dest.description.split("\n").map((para, i) =>
                para.trim() ? <p key={i}>{para}</p> : <br key={i} />
              )}
            </div>
          )}

          {/* Highlights */}
          {dest.highlights?.length > 0 && (
            <div className="dd-section">
              <h3 className="dd-section-title">
                <i className="fas fa-star"></i> Highlights
              </h3>
              <ul className="dd-list">
                {dest.highlights.map((h, i) => (
                  <li key={i}>
                    <span className="icon">⭐</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Things To Do */}
          {dest.thingsToDo?.length > 0 && (
            <div className="dd-section">
              <h3 className="dd-section-title">
                <i className="fas fa-hiking"></i> Things To Do
              </h3>
              <ul className="dd-list">
                {dest.thingsToDo.map((t, i) => (
                  <li key={i}>
                    <span className="icon">✓</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Travel Tips */}
          {dest.travelTips?.length > 0 && (
            <div className="dd-section">
              <h3 className="dd-section-title">
                <i className="fas fa-lightbulb"></i> Travel Tips
              </h3>
              <ul className="dd-list">
                {dest.travelTips.map((t, i) => (
                  <li key={i}>
                    <span className="icon">💡</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Extra images (Gallery) */}
          {images.length > 1 && (
            <div className="dd-gallery">
              <h3 className="dd-section-title">
                <i className="fas fa-images"></i> Photo Gallery
              </h3>
              <div className="dd-gallery-grid">
                {images.slice(1).map((url, i) => (
                  <div className="dd-gallery-item" key={i}>
                    <img src={url} alt={`${dest.name} gallery ${i + 1}`} loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── LINKED TOURS & PACKAGES ── */}
          <div className="dd-linked-tours">
            <h3 className="dd-section-title">
              <i className="fas fa-map-marked-alt"></i> Available Tours & Packages
            </h3>
            
            {toursLoading ? (
              <div style={{ textAlign: "center", padding: "2rem", color: "#64748b" }}>
                <div className="dd-loading-spinner" style={{ margin: "0 auto 12px", width: 36, height: 36, borderWidth: 3 }}></div>
                Loading tours & packages...
              </div>
            ) : linkedTours.length > 0 ? (
              linkedTours.map((tour) => {
                const isOpen = expandedTour === tour.id;
                return (
                  <div key={tour.id} className="dd-tour-block">
                    {/* Tour header */}
                    <div
                      className={`dd-tour-header ${isOpen ? "open" : ""}`}
                      onClick={() => setExpandedTour((prev) => (prev === tour.id ? null : tour.id))}
                    >
                      {tour.imageUrls?.[0] && (
                        <img src={tour.imageUrls[0]} alt={tour.tourName} className="dd-tour-thumb" />
                      )}
                      <div className="dd-tour-header-info">
                        <span className="dd-tour-name">{tour.tourName}</span>
                        {tour.packages?.length > 0 && (
                          <span className="dd-tour-pkg-count">
                            {tour.packages.length} Package{tour.packages.length > 1 ? "s" : ""} available
                          </span>
                        )}
                      </div>
                      <span className="dd-tour-chevron">{isOpen ? "▲" : "▼"}</span>
                    </div>

                    {/* Packages */}
                    {isOpen && (
                      <div className="dd-tour-packages">
                        {!tour.packages || tour.packages.length === 0 ? (
                          <p style={{ color: "#94a3b8", fontSize: "0.9rem", textAlign: "center", padding: "1rem" }}>
                            No active packages for this tour.
                          </p>
                        ) : (
                          <div className="dd-pkg-grid">
                            {tour.packages.map((pkg) => (
                              <div key={pkg.id} className="dd-pkg-card">
                                <div className="dd-pkg-price-bar">
                                  {pkg.discountPrice ? (
                                    <>
                                      <span className="dd-pkg-disc">
                                        ₹{Number(pkg.discountPrice).toLocaleString("en-IN")}
                                      </span>
                                      <span className="dd-pkg-orig">
                                        ₹{Number(pkg.price).toLocaleString("en-IN")}
                                      </span>
                                      <span className="dd-pkg-save">
                                        Save ₹{(Number(pkg.price) - Number(pkg.discountPrice)).toLocaleString("en-IN")}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="dd-pkg-disc">
                                      {pkg.price ? `₹${Number(pkg.price).toLocaleString("en-IN")}` : "Price on request"}
                                    </span>
                                  )}
                                </div>
                                <div className="dd-pkg-body">
                                  <h5 className="dd-pkg-name">{pkg.packageName}</h5>
                                  <div className="dd-pkg-chips">
                                    {pkg.destination && <span className="dd-pkg-chip">📍 {pkg.destination}</span>}
                                    {pkg.duration && <span className="dd-pkg-chip">🕐 {pkg.duration}</span>}
                                  </div>
                                  {pkg.description && (
                                    <p className="dd-pkg-desc">
                                      {pkg.description.length > 80 ? pkg.description.slice(0, 80) + "…" : pkg.description}
                                    </p>
                                  )}
                                  {pkg.inclusions && (
                                    <div className="dd-pkg-inc">
                                      <span className="dd-pkg-inc-label">✅ Includes:</span>
                                      <span className="dd-pkg-inc-text">
                                        {pkg.inclusions.length > 60 ? pkg.inclusions.slice(0, 60) + "…" : pkg.inclusions}
                                      </span>
                                    </div>
                                  )}
                                  <button className="dd-pkg-btn" onClick={() => handleEnquire(pkg, tour.tourName)}>
                                    Enquire Now →
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              /* ── WhatsApp CTA if no tours ── */
              <div style={{ background: "linear-gradient(135deg,#f0fdf4,#dcfce7)", border: "1px solid #bbf7d0", borderRadius: 20, padding: "2rem", textAlign: "center", marginTop: "1rem" }}>
                <p style={{ fontSize: "1.1rem", color: "#1a2a3f", fontWeight: 600, marginBottom: "1rem" }}>
                  Interested in visiting {dest.name}?
                </p>
                <button
                  onClick={handleGeneralEnquire}
                  style={{ background: "linear-gradient(135deg,#16a34a,#22c55e)", color: "#fff", border: "none", padding: "12px 28px", borderRadius: 50, fontWeight: 700, fontSize: "1rem", cursor: "pointer", transition: "all 0.3s" }}
                >
                  💬 Enquire on WhatsApp
                </button>
              </div>
            )}
          </div>

          {/* Back to destinations */}
          <div className="dd-back-row">
            <button className="dd-back-all-btn" onClick={() => navigate("/destinations")}>
              <i className="fas fa-arrow-left"></i> Back to All Destinations
            </button>
          </div>
        </div>
      </main>

      {/* ── FOOTER ── */}
      <Footer />
    </div>
  );
};

export default DestinationDetail;